import React from 'react';
import PropTypes from 'prop-types';
import styled from 'styled-components';

import BlockField from '../BlockField';
import FieldModes from '../../../../constants/FieldModes';

const StyledWrapper = styled.div`
  height: 100%;
  width: 100%;
  display: flex;
  flex-direction: column;
  justify-content: center;
  ${(props) => (typeof props.margin === 'number' ? `padding: ${props.margin}px;` : '')}
`;

const StyledTextarea = styled.textarea`
  &:focus {
    outline: none;
  }
  &:active {
    outline: none;
  }
`;

class TableBlockField extends React.PureComponent {

  static propTypes = {
    fieldId: PropTypes.string,
    fieldIdentifier: PropTypes.string,
    fieldPositionId: PropTypes.string,
    fieldMode: PropTypes.string,
    fieldPresentationMode: PropTypes.string,
    pageLayoutMode: PropTypes.string,

    value: PropTypes.array,
    cellValue: PropTypes.string,
    rowId: PropTypes.string,

    calculatedCellValue: PropTypes.string,
    disabled: PropTypes.bool,
    tabIndex: PropTypes.number,

    //SDK Styles
    primaryColor: PropTypes.string,

    //Field style
    fontSize: PropTypes.number,
    fontColor: PropTypes.string,
    fontStyle: PropTypes.string,
    fontWeight: PropTypes.string,
    textAlign: PropTypes.string,
    textTransform: PropTypes.string,
    textDecoration: PropTypes.string,
    textOverflow: PropTypes.string,
    borderColor: PropTypes.string,
    borderRadius: PropTypes.number,
    borderWidth: PropTypes.number,
    backgroundColor: PropTypes.string,
    padding: PropTypes.number,
    margin: PropTypes.number,

    //Action handlers
    // onChange: PropTypes.func,
    onFocus: PropTypes.func,
    onBlur: PropTypes.func,
  }

  static defaultProps = {
    value: ''
  }

  constructor(props) {
    super(props);
    const { cellValue } = props;
    this.state = { focused: false, changes: cellValue };
  }

  componentDidUpdate(prevProps) {
    const { changes } = this.state;
    const { cellValue } = this.props;
    if (cellValue !== prevProps.cellValue && cellValue !== changes) this.setState({ changes: cellValue });
  }

  componentWillUnmount() {

    const { cellValue, disabled, fieldId, fieldIdentifier, fieldPositionId, rowId, columnIdentifier, column, onBlur } = this.props;
    const { changes, focused } = this.state;

    /**
     * This ensures that changes are saved when this component unmounts
     * without first losing focus. The return func in useEffect is the
     * same as firing cleanup stuff in comoponentWillUnmount.
     */
    if (focused && changes !== cellValue && !disabled) {

      this.handleCellChange(changes);

      onBlur(fieldId, fieldIdentifier, fieldPositionId, {}, rowId, column, columnIdentifier);
    }

  }

  render() {
    const { focused, changes } = this.state;

    const {
      fieldId,
      fieldIdentifier,
      fieldPositionId,

      rowId,
      column,
      columnIdentifier,

      cellValue,
      calculatedCellValue,
      disabled,
      tabIndex,

      //Field style
      primaryColor,
      fontSize,
      fontColor,
      fontStyle,
      fontWeight,
      textAlign,
      textTransform,
      textDecoration,
      textOverflow,
      borderColor,
      borderRadius,
      borderWidth,
      backgroundColor,
      padding,
      margin,

      fieldMode,
      fieldPresentationMode,
      pageLayoutMode,

      onFocus,
      onBlur
    } = this.props;

    return (
      <StyledWrapper {...{ margin }}>
        {fieldMode !== FieldModes.edit ? (
          <BlockField
            key={`${fieldPositionId}-table-block-field-calculated`}
            data-testid={`${fieldPositionId}-table-block-field-calculated`}
            tabIndex={tabIndex}
            value={calculatedCellValue || ''}
            {...{

              disabled,
              primaryColor,
              fontSize,
              fontColor,
              fontStyle,
              fontWeight,
              textAlign,
              textTransform,
              textDecoration,
              textOverflow,
              borderColor,
              borderRadius,
              borderWidth,
              backgroundColor,
              padding,
              fieldMode,
              fieldPresentationMode,
              pageLayoutMode
            }}
            onFocus={() => {
              if (!focused) this.setState({ focused: true });
            }}
          />
        ) : (
          <StyledTextarea
            tabIndex={tabIndex}
            value={changes}
            disabled={disabled}
            style={{
              border: borderWidth ? `${borderWidth}px solid ${borderColor}` : 'none',
              borderRadius: borderRadius ? `${borderRadius}px` : '0px',
              padding: padding ? `${padding}px` : '2px',
              backgroundColor: backgroundColor || 'transparent',
              fontSize: fontSize || 'inherit',
              color: fontColor || 'inherit',
              fontStyle: fontStyle || 'normal',
              fontWeight: fontWeight || 'normal',
              textAlign: textAlign || 'left',
              textTransform: textTransform || 'none',
              textDecoration: textDecoration || 'none',
              textOverflow: textOverflow || 'clip',
              resize: 'none', // Disable resizing if required
              width: '100%',
              height: '100%',
              overflow: textOverflow ? 'hidden' : 'visible',
            }}
            onChange={(e) => {
              this.setState({ changes: e.target.value });
            }}
            onFocus={(e) => {

              if (!focused) this.setState({ focused: true });

              if (!disabled && onFocus) {
                onFocus(fieldId, fieldIdentifier, fieldPositionId, { blur: () => { e.target.blur(); } }, rowId, column, columnIdentifier);
              }

            }}
            onBlur={(e) => {

              const nextValue = e.target.value;

              if (focused) this.setState({ focused: false });

              if (!disabled && nextValue !== cellValue) this.handleCellChange(nextValue);

              if (onBlur) {

                onBlur(fieldId, fieldIdentifier, fieldPositionId, {}, rowId, column, columnIdentifier);

              }
            }}
          />
        )}
      </StyledWrapper>
    );
  }

  handleCellChange = (nextCellValue) => {

    const { onChange, rowId, column, value, fieldId, fieldIdentifier, fieldPositionId } = this.props;

    const nextValue = [...value];

    const targetRowIndex = value.findIndex((row) => row._id === rowId);
    const nextRow = { ...nextValue[targetRowIndex] };

    /**<----------NOTICE---------------->
     * onChange will only be triggered if the row and column associated
     * with the table cell is undeleted
    */
    if (nextRow && nextRow._id) {

      nextRow.cells = { ...nextRow.cells, [column]: nextCellValue };

      nextValue[targetRowIndex] = nextRow;

      if (onChange) onChange(fieldId, fieldIdentifier, fieldPositionId, nextValue);

    }
    else {
      this.setState({ changes: '' });
    }

  }

}

export default TableBlockField;
